import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { IOrderDashboardRow } from '../../../interfaces/garments/merchReport.interface';
import { useAuth } from '../../../context/AuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { enumLabel } from '../../../utils/garmentsLabels';
import { isLate, journeyPath, shortDate, sortThreadRows } from './threadData';

const MAX_TASKS = 5;
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/**
 * The merchandiser's task list: orders with an overdue T&A step, worst first, each with the next
 * step that is waiting. Derived from the same order rows as the threads, so it adds no request.
 */
export default function NeedsActionPanel({ rows }: { rows: IOrderDashboardRow[] }) {
    const { hasPermission } = useAuth();
    const canOpen = hasPermission(PERMISSIONS.BUYER_ORDER_VIEW);
    const late = useMemo(() => sortThreadRows(rows).filter(isLate), [rows]);
    const shown = late.slice(0, MAX_TASKS);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, minWidth: 0 }} data-testid="thread-tasks" data-help-section="dashboard.thread.tasks">
            <Typography variant="subtitle2" fontWeight={700}>Needs action</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Orders with an overdue T&amp;A step</Typography>
            {shown.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nothing is overdue. All live orders are on plan.</Typography>
            ) : (
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}>
                    {shown.map(r => (
                        <Box component="li" key={r.orderId}>
                            <Box
                                {...(canOpen ? { component: RouterLink, to: journeyPath(r.orderId) } : {})}
                                data-help="dashboard.thread.taskRow"
                                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1, py: 0.75, borderRadius: 1, color: 'inherit', textDecoration: 'none', ...(canOpen ? { '&:hover': { bgcolor: 'action.hover' } } : {}) }}
                            >
                                <WarningAmberIcon fontSize="small" color="error" aria-hidden sx={{ mt: 0.25 }} />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                                        {r.orderNumber} · {r.buyerName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                        {r.overdueCount > 0 ? `${plural(r.overdueCount, 'step')} overdue` : 'Running late'}
                                        {r.nextMilestoneType ? ` · next: ${enumLabel(r.nextMilestoneType)}` : ''}
                                        {r.nextMilestoneTargetDate ? ` due ${shortDate(r.nextMilestoneTargetDate)}` : ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
            {late.length > shown.length && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {late.length - shown.length} more late orders are in the order threads below.
                </Typography>
            )}
        </Paper>
    );
}
